import React, { useState, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  showUnsavedChangesSheet,
  isUnsavedChangesSheetDismissed,
  leftFromBackButton,
  resetLeaveFromBackButton
} from 'ducks/sheets';
import { getInvalidProducts, getProductEntities } from 'ducks/core';
import { isConfigurationPossible } from 'ducks/layout';
import { getVpcCode } from 'ducks/vpc';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { isKiosk } from 'utils/platform';
import { useAppDispatch } from 'ducks/hooks';
import { useCore } from './core';
import { e2e } from 'utils/queryUtils';
import { useServiceSettings } from './serviceSettings';

type NextLoaction = {
  pathname: AppRoutes;
};

interface NavigationBlockContextProps {
  leaveRoute: () => void;
  handleBlockedNavigation: (nextLoaction: NextLoaction) => boolean;
  prompt: any;
}

const NavigationBlockContext = React.createContext<NavigationBlockContextProps>({
  leaveRoute: () => null,
  handleBlockedNavigation: () => true,
  prompt: null
});

interface Props {
  children: React.ReactNode;
}

export const useNavigationBlock = () => useContext(NavigationBlockContext);

type LeaveRoute = () => void;

export const NavigationBlockProvider = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const productEntitiesInScene = useSelector(getProductEntities);
  const invalidProducts = useSelector(getInvalidProducts);
  const dismissed = useSelector(isUnsavedChangesSheetDismissed);
  const vpc = useSelector(getVpcCode);
  const configurationPossible = useSelector(isConfigurationPossible);
  const history = useHistory();
  const [leaveRoute, setLeaveRoute] = useState<LeaveRoute>(() => {});
  const userLeftFromBackButton = useSelector(leftFromBackButton);
  const coreApi = useCore();

  const {
    dexfSettings: {
      kompis: {
        urls: { homePageLink }
      }
    }
  } = useServiceSettings();
  const location = useLocation();

  const handleBlockedNavigation = useCallback(
    (nextLocation: any): boolean => {
      const { pathname } = nextLocation;
      const leaveFn = () => {
        if (pathname === AppRoutes.IKEA_HOME && homePageLink) {
          return () => (window.location.href = homePageLink);
        }
        return () => history.push(pathname);
      };

      const entities = coreApi.entities.getAllProductEntities();

      setLeaveRoute(leaveFn);

      if (!configurationPossible || invalidProducts.length > 0) {
        return true;
      }

      // Back to gallery
      if (
        pathname === AppRoutes.PLANNER_NEW ||
        pathname === AppRoutes.IKEA_HOME ||
        (pathname === AppRoutes.LANDING_PAGE && isKiosk()) ||
        (pathname === AppRoutes.HOME && isKiosk())
      ) {
        // User pressed "Leave" in modal
        if (dismissed) {
          return true;
        }
        // No products in the scene
        if (productEntitiesInScene.length === 0) {
          if (pathname === AppRoutes.IKEA_HOME) {
            leaveFn()();
          }
          return true;
        }

        // If Kiosk and going from Summary to Landing Page -> should not show unsaved changed
        if (
          isKiosk() &&
          location.pathname === AppRoutes.SUMMARY &&
          nextLocation.pathname === AppRoutes.LANDING_PAGE
        ) {
          return true;
        }

        // No vpc, i.e unsaved changes
        // On summary always show unsaved changes
        if (!vpc || location.pathname === AppRoutes.SUMMARY) {
          const shouldVerifyUnsaved = !e2e() && isKiosk();

          if (
            (shouldVerifyUnsaved && userLeftFromBackButton && entities.length !== 0) ||
            (isKiosk() && nextLocation.pathname === AppRoutes.HOME && entities.length !== 0)
          ) {
            dispatch(showUnsavedChangesSheet());
            return false;
          } else if (
            !userLeftFromBackButton &&
            location.pathname !== nextLocation &&
            entities.length !== 0 &&
            nextLocation.pathname !== AppRoutes.HOME &&
            location.pathname !== AppRoutes.PLANNER_SCREENSAVER
          ) {
            dispatch(showUnsavedChangesSheet());
            return false;
          } else {
            dispatch(resetLeaveFromBackButton());
          }
        }
      }

      if (pathname === AppRoutes.IKEA_HOME) {
        leaveFn()();
      }

      return true;
    },
    [
      configurationPossible,
      coreApi,
      invalidProducts.length,
      vpc,
      productEntitiesInScene.length,
      dismissed,
      homePageLink,
      history,
      userLeftFromBackButton,
      location,
      dispatch
    ]
  );

  const UnsavedChangesPrompt = () => <Prompt when={true} message={handleBlockedNavigation} />;

  return (
    <NavigationBlockContext.Provider
      value={{ handleBlockedNavigation, leaveRoute, prompt: UnsavedChangesPrompt }}
    >
      {children}
    </NavigationBlockContext.Provider>
  );
};
